import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#b45309", amberBg: "#fffbeb", amberBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:      <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:     <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Search:         <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    MapPin:         <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Star:           <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    CheckCircle:    <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:     <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    ShieldCheck:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    Calendar:       <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    Clock:          <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Wallet:         <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    Zap:            <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Users:          <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    ChevronDown:    <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:      <><path d="m18 15-6-6-6 6"/></>,
    ShoppingBag:    <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" x2="21" y1="6" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></>,
    ShoppingCart:   <><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></>,
    Package:        <><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7"/><path d="m7.5 4.27 9 5.15"/></>,
    FileText:       <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
    Truck:          <><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3"/><rect width="7" height="7" x="14" y="10" rx="1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
    Navigation:     <><polygon points="3 11 22 2 13 21 11 13 3 11"/></>,
    Briefcase:      <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    MessageCircle:  <><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    Coffee:         <><path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" x2="6" y1="2" y2="4"/><line x1="10" x2="10" y1="2" y2="4"/><line x1="14" x2="14" y1="2" y2="4"/></>,
    Building:       <><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M8 10h.01M8 14h.01M16 10h.01M16 14h.01"/></>,
    Phone:          <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    X:              <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
    Info:           <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Location suggestions ── */
const LOCATIONS = [
  "Westlands, Nairobi", "Kilimani, Nairobi", "Karen, Nairobi",
  "Lavington, Nairobi", "Parklands, Nairobi", "South B, Nairobi",
  "Langata, Nairobi", "Kasarani, Nairobi", "Ruaka, Nairobi",
  "CBD, Nairobi", "Upperhill, Nairobi", "Eastleigh, Nairobi",
  "Nyali, Mombasa", "Bamburi, Mombasa", "Mombasa CBD",
  "Kisumu CBD", "Milimani, Kisumu",
  "Nakuru CBD", "Eldoret CBD", "Thika",
];

/* ── Services ── */
const SERVICES = [
  {
    icon: "ShoppingCart",
    label: "Grocery and Market Shopping",
    sub: "Supermarket, market, or both",
    range: "KES 300 – 800",
    note: "runner fee · groceries billed separately",
    desc: "Your JiraniPro runner goes to your preferred supermarket or local market, buys exactly what you list, sends you photos of items and receipts, and delivers to your door. You pay for the groceries through M-Pesa transfer to the runner or directly reimburse. Runner fee covers their time and transport.",
    popular: true,
  },
  {
    icon: "Building",
    label: "Government and Office Errands",
    sub: "Huduma Centre, banks, embassies, KRA",
    range: "KES 500 – 2,000",
    note: "per errand depending on complexity",
    desc: "Queue at Huduma Centre, collect documents, drop off applications, pay utility bills in person, or handle any errand that requires someone physically present at an office or government facility. The runner updates you in real time via the app.",
    popular: true,
  },
  {
    icon: "Package",
    label: "Package Pickup and Drop-off",
    sub: "Collect from stores, deliver to recipients",
    range: "KES 300 – 1,200",
    note: "per trip within the city",
    desc: "Send a runner to collect your online order, pick up a repaired item, receive a parcel from a friend, or deliver something to a recipient across town. The runner confirms collection and delivery with a photo sent through the app.",
    popular: false,
  },
  {
    icon: "FileText",
    label: "Document Collection and Delivery",
    sub: "Contracts, certificates, official papers",
    range: "KES 400 – 1,000",
    note: "per trip",
    desc: "Need a signed document delivered across Nairobi? A certificate collected from an institution? An application dropped off before a deadline? Send a trusted, verified JiraniPro runner instead of going yourself.",
    popular: false,
  },
  {
    icon: "ShoppingBag",
    label: "Stock and Supplies for Businesses",
    sub: "Restaurants, salons, offices, shops",
    range: "KES 500 – 2,500",
    note: "per run depending on volume",
    desc: "Running low on stock at your restaurant, salon, or small shop? Send a runner to your supplier, wholesaler, or distributor. They collect, confirm quantities, and deliver. Useful for urgent restocking when you cannot leave your premises.",
    popular: true,
  },
  {
    icon: "Coffee",
    label: "Personal Household Errands",
    sub: "Prescriptions, dry cleaning, hardware, gifts",
    range: "KES 300 – 700",
    note: "runner fee per errand",
    desc: "Collect a prescription from the pharmacy. Pick up your dry cleaning. Buy a specific item from a hardware store. Get a birthday gift wrapped and delivered. Any single errand that just needs someone reliable to go and do it.",
    popular: false,
  },
  {
    icon: "Truck",
    label: "Bulk and Heavy Item Delivery",
    sub: "Car-based runners for larger loads",
    range: "KES 1,000 – 4,000",
    note: "per trip depending on distance and load",
    desc: "For loads too large for a boda boda -- furniture pieces, bulk market purchases, appliances, business supplies -- book a car-based JiraniPro runner. They confirm vehicle capacity before accepting the job.",
    popular: false,
  },
  {
    icon: "Navigation",
    label: "Full-Day Errand Runner",
    sub: "Book a runner for several hours",
    range: "KES 2,500 – 6,000",
    note: "per half or full day",
    desc: "When you have more than one errand to run and want one trusted person to handle all of them, book a runner for a half-day or full day. You set the task list. They work through it, check in after each one, and handle whatever comes up.",
    popular: true,
  },
];

/* ── FAQ data ── */
const FAQS = [
  {
    q: "Who pays for the groceries or items the runner buys?",
    a: "You do, directly. The runner fee on JiraniApp covers the runner's time and transport. For the cost of groceries or any items they buy on your behalf, you transfer money to the runner through M-Pesa before or during the errand -- the runner will not spend their own money on your items. You agree the budget before they go.",
  },
  {
    q: "How do I know the runner bought the right things?",
    a: "Runners are expected to send you photos of items before purchasing where there is any doubt about which brand or size you want, and to share a photo of the receipt after. You can communicate directly through the in-app messaging at any point during the errand. If something is unavailable, they contact you before substituting.",
  },
  {
    q: "What if the runner needs to handle cash on my behalf?",
    a: "This is common for market shopping or government office payments where card or M-Pesa is not accepted. You agree the amount upfront and transfer it via M-Pesa before the errand starts. The runner provides a receipt or photo proof for every cash transaction. If change is due, they return it on delivery or transfer it back.",
  },
  {
    q: "Can a runner handle multiple stops in one trip?",
    a: "Yes. When booking, you can list multiple stops or tasks. The runner plans the most efficient route and works through them in order. Multi-stop errands are typically booked as a half-day or full-day engagement depending on how many tasks are involved. The runner fee reflects the time and transport required.",
  },
  {
    q: "Do runners know Nairobi well enough to navigate without guidance?",
    a: "All JiraniPro runners are based in the city they operate in and are familiar with the major estates, markets, and government offices. If you are sending them somewhere specific, include the name and location in the booking notes. You can also share a Google Maps link through the in-app messaging once the job starts.",
  },
  {
    q: "I am visiting Nairobi and do not know the city. Can a runner help me get things done?",
    a: "Yes, and this is one of the most common use cases for this service. A runner can shop at local markets on your behalf, handle office errands you do not know how to navigate, collect things from parts of the city you are not familiar with, and advise on local suppliers when you ask through the app. You do not need to know the city at all.",
  },
  {
    q: "What if the runner does not complete the errand or something goes wrong?",
    a: "Open a dispute through Help and Support within 48 hours. Describe what was agreed versus what happened. Our Trust and Safety team reviews every case. If the runner failed to complete the task and you already paid the runner fee, a refund of that fee is reviewed as part of the dispute process.",
  },
  {
    q: "Can I book a runner regularly, like every week for grocery shopping?",
    a: "Yes. Once you have worked with a runner you trust, you can book recurring slots through the platform. Many clients who relocated to Nairobi or who simply prefer not to go to the market themselves book the same runner on a weekly basis. Recurring bookings are managed through your Bookings dashboard.",
  },
];

/* ── Who uses this ── */
const USE_CASES = [
  {
    label: "New to Nairobi",
    body: "You just relocated. You do not know where to buy things, which markets are reliable, or how to navigate the city's government offices. A JiraniPro runner becomes your local guide on the ground.",
  },
  {
    label: "Business owner who cannot leave",
    body: "Your salon, restaurant, or shop needs restocking urgently and you cannot close to go get it. Send a runner to your supplier and have the stock at your door within hours.",
  },
  {
    label: "Busy professional",
    body: "You work long hours. The pharmacy closes before you finish. The dry cleaning has been there for two weeks. A runner handles all of it while you stay at your desk.",
  },
  {
    label: "Parent or caregiver",
    body: "The school run, the house, the job. There is no time left for errands. A trusted runner handles the shopping list, the document drop-off, and the prescription pickup.",
  },
  {
    label: "Diaspora sending support home",
    body: "You are abroad and your family in Nairobi needs something sorted. Book a runner remotely, brief them through the app, and have them handle it on your behalf.",
  },
  {
    label: "Small business with no delivery team",
    body: "You need to send contracts, samples, or packages to clients across town on the same day. A car-based runner handles it without you needing a full-time dispatch operation.",
  },
];

/* ── Location search ── */
function LocationSearch({ onSearch }) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (value.length < 2) { setSuggestions([]); return; }
    setSuggestions(LOCATIONS.filter(l => l.toLowerCase().includes(value.toLowerCase())).slice(0, 5));
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setFocused(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (loc) => { setValue(loc); setSuggestions([]); setFocused(false); onSearch(loc); };
  const handleSubmit = () => { if (value.trim()) onSearch(value.trim()); };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div style={{
        display: "flex", alignItems: "center",
        background: T.white, borderRadius: 14,
        border: `1.5px solid ${focused ? T.g400 : T.n200}`,
        boxShadow: focused ? `0 0 0 4px ${T.g100}` : "0 2px 12px rgba(0,0,0,.08)",
        overflow: "hidden", transition: "all .2s",
      }}>
        <span style={{ paddingLeft: 18, flexShrink: 0 }}>
          <Icon name="MapPin" size={18} color={focused ? T.g500 : T.n400} strokeWidth={1.8} />
        </span>
        <input
          value={value}
          onChange={e => setValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          placeholder="Your area or city in Kenya..."
          style={{
            flex: 1, padding: "16px 14px", border: "none", outline: "none",
            fontSize: 15, fontFamily: T.sans, color: T.n900, background: "transparent",
          }}
        />
        {value && (
          <button onClick={() => { setValue(""); setSuggestions([]); }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 10px", color: T.n400, display: "flex" }}>
            <Icon name="X" size={15} strokeWidth={2} />
          </button>
        )}
        <button onClick={handleSubmit} style={{
          margin: 6, padding: "11px 20px", background: T.g500, color: T.white,
          border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
          fontFamily: T.sans, cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
          flexShrink: 0, whiteSpace: "nowrap",
        }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          <Icon name="Search" size={15} color={T.white} strokeWidth={2} />
          Find a runner
        </button>
      </div>
      {focused && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0,
          background: T.white, borderRadius: 12, border: `1px solid ${T.n150}`,
          boxShadow: "0 8px 32px rgba(0,0,0,.12)", zIndex: 100, overflow: "hidden",
        }}>
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => handleSelect(s)} style={{
              width: "100%", textAlign: "left", padding: "13px 18px",
              background: "none", border: "none", cursor: "pointer",
              fontFamily: T.sans, fontSize: 14, color: T.n700,
              display: "flex", alignItems: "center", gap: 10,
              borderBottom: i < suggestions.length - 1 ? `1px solid ${T.n100}` : "none",
              transition: "background .1s",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.n50}
              onMouseLeave={e => e.currentTarget.style.background = "none"}>
              <Icon name="MapPin" size={14} color={T.n400} strokeWidth={1.8} />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Service card ── */
function ServiceCard({ icon, label, sub, range, note, desc, popular }) {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 16, border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        transition: "all .18s", overflow: "hidden",
      }}>
      <div style={{ padding: "20px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: hovered ? T.g100 : T.n50, border: `1px solid ${hovered ? T.g200 : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s", flexShrink: 0 }}>
              <Icon name={icon} size={18} color={hovered ? T.g600 : T.n400} strokeWidth={1.7} />
            </div>
            {popular && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.g600, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "2px 8px", letterSpacing: "0.05em" }}>
                POPULAR
              </span>
            )}
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontFamily: T.serif, fontSize: 17, color: hovered ? T.g700 : T.n900, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{range}</div>
            <div style={{ fontSize: 11, color: T.n400, marginTop: 2, lineHeight: 1.4 }}>{note}</div>
          </div>
        </div>
        <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 3, letterSpacing: "-0.01em" }}>{label}</div>
        <div style={{ fontSize: 12, color: T.n400, marginBottom: 12 }}>{sub}</div>
        <button onClick={() => setExpanded(o => !o)} style={{
          display: "flex", alignItems: "center", gap: 5,
          background: "none", border: "none", cursor: "pointer",
          fontFamily: T.sans, fontSize: 12, fontWeight: 600,
          color: expanded ? T.g600 : T.n400, padding: 0, transition: "color .15s",
        }}>
          {expanded ? "Hide detail" : "How this works"}
          <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={13} strokeWidth={2.2} color={expanded ? T.g600 : T.n400} />
        </button>
      </div>
      <div style={{ overflow: "hidden", maxHeight: expanded ? 220 : 0, transition: "max-height .3s cubic-bezier(.4,0,.2,1)" }}>
        <div style={{ padding: "0 20px 20px" }}>
          <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.8, margin: 0 }}>{desc}</p>
        </div>
      </div>
    </div>
  );
}

/* ── How step ── */
function HowStep({ num, icon, title, desc, isLast }) {
  return (
    <div style={{ display: "flex", gap: 18, position: "relative" }}>
      {!isLast && (
        <div style={{ position: "absolute", left: 19, top: 44, bottom: -8, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n150})` }} />
      )}
      <div style={{ width: 40, height: 40, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={icon} size={16} color={T.g600} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 32 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 4 }}>Step {num}</div>
        <div style={{ fontFamily: T.sans, fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 6, letterSpacing: "-0.01em" }}>{title}</div>
        <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0 }}>{desc}</p>
      </div>
    </div>
  );
}

/* ── Testimonial ── */
function Testimonial({ name, area, service, stars, text }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const palette = [
    { bg: T.g50,     text: T.g700,   border: T.g100 },
    { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  ];
  const c = palette[name.charCodeAt(0) % palette.length];
  return (
    <div style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Icon key={i} name="Star" size={13} color={i < stars ? T.amber : T.n200} strokeWidth={i < stars ? 0 : 1.5} />
        ))}
      </div>
      <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>"{text}"</p>
      <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: c.bg, border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: c.text, fontFamily: T.sans, flexShrink: 0 }}>{initials}</div>
        <div>
          <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.n900 }}>{name}</div>
          <div style={{ fontSize: 12, color: T.n400 }}>{service} · {area}</div>
        </div>
      </div>
    </div>
  );
}

/* ── FAQ item ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "20px 0", background: "none", border: "none", cursor: "pointer",
        fontFamily: T.sans, textAlign: "left", gap: 16,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.45, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s", fontSize: 18, fontWeight: 300,
          transform: open ? "rotate(45deg)" : "none",
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 400 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.8, margin: "0 0 20px", paddingRight: 40 }}>{a}</p>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function ErrandsDeliveryPage() {
  useFonts();
  const navigate = useNavigate();

  const handleSearch = (location) => {
    navigate(`/search?category=errands-delivery&location=${encodeURIComponent(location)}`);
  };

  const TESTIMONIALS = [
    {
      name: "Priya N.", area: "Westlands, Nairobi", service: "Grocery Shopping", stars: 5,
      text: "I moved to Nairobi six months ago and still do not know my way around the markets. My runner has been doing my weekly shopping at Toi Market and two supermarkets every Saturday morning. She knows what I want, messages me if something is out of stock, and is always at my door by noon.",
    },
    {
      name: "Kevin O.", area: "Upperhill, Nairobi", service: "Business Stock Run", stars: 5,
      text: "I run a small restaurant in Upperhill. When we run low on ingredients mid-service I cannot leave. I booked a runner for the first time out of desperation and it worked perfectly. He went to three suppliers, sent me receipts, and was back in under two hours. Now I use this regularly.",
    },
    {
      name: "Amina S.", area: "Kilimani, Nairobi", service: "Office Errands", stars: 5,
      text: "I needed to submit documents at Huduma Centre and could not take the day off work. My runner went, queued, submitted everything, and sent me a photo of the confirmation slip within three hours. Exactly what I needed.",
    },
  ];

  const HOW_STEPS = [
    {
      num: "01", icon: "FileText", title: "Describe your errand in detail",
      desc: "When booking, use the notes field to list exactly what you need done -- the shopping list, the office address, the document to collect, the supplier name and what to buy. The more specific your instructions, the faster and more accurately the runner can work.",
    },
    {
      num: "02", icon: "Users", title: "A verified local runner is assigned",
      desc: "JiraniApp matches your errand to a verified runner available in your area. You see their profile, rating, and past client reviews before confirming. For cash-involved errands, you agree the budget and transfer funds via M-Pesa before they head out.",
    },
    {
      num: "03", icon: "MessageCircle", title: "Track progress through the app",
      desc: "Your runner checks in after each stop, sends photos of receipts and items, and messages you if anything is unavailable or needs a decision. You stay informed without having to be there. No chasing calls.",
    },
    {
      num: "04", icon: "Wallet", title: "Confirm delivery and pay the runner fee",
      desc: "Your JiraniApp runner fee is held securely and released when you confirm the errand is complete. Grocery costs and any cash floats are handled separately via M-Pesa transfer agreed before the run. You receive a summary of every stop and every receipt.",
    },
  ];

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`* { box-sizing: border-box; } ::-webkit-scrollbar { display: none; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={() => navigate("/become-a-pro")} style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          background: "none", border: `1px solid ${T.n200}`,
          color: T.n600, fontSize: 13, fontFamily: T.sans,
          padding: "8px 16px", borderRadius: 8, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap",
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.g400; e.currentTarget.style.color = T.g600; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          <Icon name="Navigation" size={13} strokeWidth={2} />
          Become an errand runner
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(52px,7vw,96px) 20px clamp(44px,6vw,72px)", borderBottom: `1px solid ${T.n100}`, background: `linear-gradient(180deg, ${T.g50} 0%, ${T.white} 100%)` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 22 }}>
            <Icon name="Navigation" size={14} color={T.g500} strokeWidth={2.2} />
            Errands and Delivery
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,68px)", fontWeight: 400, margin: "0 0 20px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 620 }}>
            Someone who knows<br/>
            the city, handling<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>your errands for you</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.8, margin: "0 0 14px", maxWidth: 500 }}>
            Whether you are new to Nairobi, too busy to leave, or running a business that needs someone on the ground -- JiraniApp connects you with verified local runners who handle your errands, shopping, deliveries, and office tasks while you get on with your day.
          </p>
          <p style={{ fontSize: 14, color: T.n400, margin: "0 0 32px", lineHeight: 1.6 }}>
            Boda boda runners for quick errands. Car-based couriers for heavier loads. Full-day runners for complex task lists.
          </p>
          <div style={{ maxWidth: 560 }}>
            <LocationSearch onSearch={handleSearch} />
            <p style={{ fontSize: 12, color: T.n400, margin: "10px 0 0" }}>
              Available across Nairobi, Mombasa, Kisumu, Nakuru, Eldoret, Thika and surrounding areas
            </p>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "BadgeCheck", label: "All runners ID-verified",        sub: "local, trusted, and reviewed" },
            { icon: "MessageCircle", label: "Live updates during the errand", sub: "photos, receipts, check-ins" },
            { icon: "Wallet",     label: "Runner fee held securely",        sub: "released only when you confirm done" },
          ].map(({ icon, label, sub }, i) => (
            <div key={i} style={{ padding: "20px 16px", display: "flex", alignItems: "flex-start", gap: 11, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={17} color={T.g500} strokeWidth={1.6} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n800, lineHeight: 1.3, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 12, color: T.n400 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ══ WHO THIS IS FOR ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,64px)", marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Who uses this</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            You do not need to know<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>the city to get things done</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Errands and Delivery on JiraniApp serves a wide range of people with the same core need: a trusted, local person who can handle things on the ground when you cannot.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 0 }}>
            {USE_CASES.map((item, i) => (
              <div key={i} style={{ padding: "22px 0", borderBottom: `1px solid ${T.n100}`, display: "flex", gap: 14 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.g400, flexShrink: 0, marginTop: 8 }} />
                <div>
                  <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 6, letterSpacing: "-0.01em" }}>{item.label}</div>
                  <p style={{ fontFamily: T.sans, fontSize: 13, color: T.n600, lineHeight: 1.75, margin: 0 }}>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ SERVICES GRID ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>What you can book</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Eight types of errand,<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>one place to book them</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Runner fees shown are the service charge paid through JiraniApp. Grocery costs and any cash purchases are agreed separately and sent to the runner via M-Pesa before the errand starts.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
            {SERVICES.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>

          <div style={{ marginTop: 20, padding: "14px 18px", borderRadius: 10, background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", gap: 10 }}>
            <Icon name="Info" size={16} color={T.amber} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontFamily: T.sans, fontSize: 13, color: "#92400e", margin: 0, lineHeight: 1.65 }}>
              Runner fees cover time and transport only. The cost of any items purchased on your behalf is additional and agreed with the runner before they head out. All cash transactions are documented with receipts or photos sent through the app.
            </p>
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>How it works</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Brief once, then<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>let the runner handle it</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 32px", lineHeight: 1.65 }}>
            The less you have to think about it after booking, the better we have done our job.
          </p>
          <div style={{ paddingLeft: 4 }}>
            {HOW_STEPS.map((step, i) => (
              <HowStep key={i} {...step} isLast={i === HOW_STEPS.length - 1} />
            ))}
          </div>
          <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => handleSearch("")} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "13px 26px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Find a runner near you
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/how-it-works")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n500, fontSize: 13, fontFamily: T.sans, padding: 0 }}>
              Full platform guide <Icon name="ArrowRight" size={13} strokeWidth={2} color={T.n400} />
            </button>
          </div>
        </div>

        {/* ══ TRUST SECTION ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)", padding: "clamp(28px,5vw,40px)", borderRadius: 18, background: T.g50, border: `1px solid ${T.g100}` }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Jirani Guarantee</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            A stranger handling your errands<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>requires real trust. We take that seriously.</em>
          </h2>
          <p style={{ fontFamily: T.sans, fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 24px", maxWidth: 500 }}>
            Every runner on JiraniApp is identity-verified before they can accept bookings. All cash-involved errands are documented. All communication stays in the app. If something goes wrong, the Jirani Guarantee covers you.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {[
              { icon: "BadgeCheck", title: "ID-verified runners", body: "Every runner has submitted and passed identity verification. You know who is handling your errands." },
              { icon: "MessageCircle", title: "Everything documented", body: "Receipts, photos, check-ins. Every step of your errand is captured and sent to you through the app." },
              { icon: "ShieldCheck", title: "Dispute protection", body: "If an errand was not completed as agreed, raise a dispute within 48 hours and our team reviews it fairly." },
              { icon: "Wallet", title: "Runner fee protected", body: "The fee you pay through JiraniApp is held until you confirm the errand is done. No confirmation, no release." },
            ].map((item, i) => (
              <div key={i} style={{ padding: "18px", borderRadius: 12, background: T.white, border: `1px solid ${T.g100}`, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={15} color={T.g500} strokeWidth={1.8} />
                </div>
                <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.g800 }}>{item.title}</div>
                <p style={{ fontFamily: T.sans, fontSize: 12, color: T.g700, lineHeight: 1.7, margin: 0 }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>From clients</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What happens when<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>it actually works</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>From clients who now use JiraniApp runners regularly.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {TESTIMONIALS.map((t, i) => <Testimonial key={i} {...t} />)}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Common questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Before you <em style={{ fontStyle: "italic", color: T.g600 }}>book a runner</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>The questions new clients ask most.</p>
          <div style={{ marginTop: 8 }}>
            {FAQS.map((f, i) => <FaqItem key={i} {...f} />)}
          </div>
        </div>

        {/* ══ RUNNER RECRUITMENT STRIP ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)", padding: "24px 28px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.n50, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: T.white, border: `1px solid ${T.n200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="Navigation" size={18} color={T.n600} strokeWidth={1.7} />
            </div>
            <div>
              <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 3 }}>Do you know your city and want to earn from it?</div>
              <div style={{ fontFamily: T.sans, fontSize: 13, color: T.n400 }}>Join JiraniApp as a runner. Set your own hours. Get paid via M-Pesa.</div>
            </div>
          </div>
          <button onClick={() => navigate("/become-a-pro")} style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: T.n900, color: T.white, border: "none",
            padding: "11px 20px", borderRadius: 9, fontSize: 13, fontWeight: 600,
            fontFamily: T.sans, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
          }}
            onMouseEnter={e => e.currentTarget.style.background = T.n800}
            onMouseLeave={e => e.currentTarget.style.background = T.n900}>
            Join as a JiraniPro
            <Icon name="ArrowRight" size={13} color={T.white} strokeWidth={2} />
          </button>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,52px)", borderRadius: 20, border: `1px solid ${T.g100}`, textAlign: "center", background: T.g50 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.white, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Zap" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            A trusted runner is<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>available in your area today</em>
          </h2>
          <p style={{ fontFamily: T.sans, fontSize: 15, color: T.n400, margin: "0 auto 28px", lineHeight: 1.75, maxWidth: 380 }}>
            Tell them what needs doing. They handle it. You get on with your day.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => handleSearch("")} style={{
              padding: "14px 32px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Find a runner near you
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/how-it-works")} style={{
              padding: "14px 28px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              How JiraniApp works
            </button>
          </div>
          <p style={{ fontFamily: T.sans, fontSize: 12, color: T.n300, margin: "18px 0 0" }}>
            Verified runners · M-Pesa payment · Jirani Guarantee on every errand
          </p>
        </div>

      </div>
    </div>
  );
}